import * as React from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  MessageSeparator,
  Avatar,
} from "@chatscope/chat-ui-kit-react";
import { NegotiationStatus, Project, ProjectStatus } from "../../types/project";
import {
  useGetChatMessagesQuery,
  useJoinChatMutation,
  useSendChatMessageMutation,
} from "../../services/chat.service";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import { ChatOutlined, Refresh } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useFilePicker } from "use-file-picker";
import { ROLE_TALENT_ID } from "../../configs/api";
import { useUILabel } from "../../hooks/useUILabel";

const ProjectMessagesPanel = ({ project }: { project: Project; }) => {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chatId = project.chats?.[0]?.id || 0;
  const unableToFindTalentLabel = useUILabel("project.unableToFindTalent");
  const joinedChatRoomLabel = useUILabel("project.joinedChatRoom");
  const fileSizeTooSmallLabel = useUILabel("project.fileSizeTooSmall");
  const fileSizeTooLargeLabel = useUILabel("project.fileSizeTooLarge");
  const problemReadingFileLabel = useUILabel("project.problemReadingFile");
  const tooManyFilesLabel = useUILabel("project.tooManyFiles");
  const notEnoughFilesLabel = useUILabel("project.notEnoughFiles");
  const refreshLabel = useUILabel("common.refresh");
  const joinChatLabel = useUILabel("project.joinChat");
  const typeMessageHereLabel = useUILabel("project.typeMessageHere");
  const sanitizeMessageBody = React.useCallback((body?: string) => {
    if (!body) return "";
    if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
      return body;
    }
    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(body, "text/html");
      Array.from(
        doc.querySelectorAll("script, style, link, meta, iframe, object, embed")
      ).forEach((node) => node.remove());
      Array.from(doc.querySelectorAll("*")).forEach((node) => {
        // Remove inline event handlers such as onclick
        Array.from(node.attributes).forEach((attr) => {
          if (/^on/i.test(attr.name)) {
            node.removeAttribute(attr.name);
          }
        });
      });
      return doc.body.innerHTML;
    } catch (_err) {
      return body.replace(/<script.*?>.*?<\/script>/gi, "");
    }
  }, []);
  const [openFileSelector, { plainFiles, loading, errors, clear }] =
    useFilePicker({
      multiple: false,
      accept: [
        ".doc",
        ".docx",
        ".pdf",
        ".txt",
        ".jpeg",
        ".jpg",
        ".png",
        ".gif",
        ".pptx",
        ".zip",
        ".xls",
        "xlsx",
        ".csv",
      ],
      maxFileSize: 50,
      readFilesContent: false,
    });

  const { data, isLoading, isFetching, refetch, isError, error } =
    useGetChatMessagesQuery(
      {
        projectId: project.id,
        chatId,
      },
      { skip: chatId == 0 }
    );
  const [doSend, { isLoading: sending }] = useSendChatMessageMutation();
  const [doJoin, { isLoading: joining }] = useJoinChatMutation();

  const _send = async (text: string, file?: File) => {
    try {
      if (text?.length == 0) return;
      await doSend({
        body: text,
        chat_id: chatId,
        project_id: project.id,
        file,
      }).unwrap();
      clear();
    } catch (err: any) {
      toast.error(err.message);
      clear();
    }
  };
  // @ts-ignore
  const onSend = React.useCallback((innerHtml, text, innerText: any) => {
    if (innerText?.length == 0) return;
    _send(innerText);
  }, []);

  const handleJoinChat = async () => {
    try {
      //get one resource_id before joining (API requirement)
      const res_id = project?.resources?.find((it) =>
        [NegotiationStatus.HIRED, NegotiationStatus.COMPLETED].includes(
          it.status
        )
      )?.resource_id;
      if (!res_id) {
        toast.error(unableToFindTalentLabel);
        return;
      }
      await doJoin({ project_id: project?.id!, resource_id: res_id! }).unwrap();
      toast.success(joinedChatRoomLabel);
      refetch();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  React.useEffect(() => {
    if (plainFiles?.length > 0) {
      const file = plainFiles[0];
      _send(file.name, file);
    }
  }, [plainFiles]);

  React.useEffect(() => {
    errors?.[0]?.fileSizeTooSmall && toast.error(fileSizeTooSmallLabel);
    errors?.[0]?.fileSizeToolarge && toast.error(fileSizeTooLargeLabel);
    errors?.[0]?.readerError &&
      toast.error(problemReadingFileLabel);
    errors?.[0]?.maxLimitExceeded && toast.error(tooManyFilesLabel);
    errors?.[0]?.minLimitNotReached && toast.error(notEnoughFilesLabel);
  }, [errors, fileSizeTooSmallLabel, fileSizeTooLargeLabel, problemReadingFileLabel, tooManyFilesLabel, notEnoughFilesLabel]);

  const Messages = React.useMemo(() => {
    let lastDate = "";
    return data?.data
      ?.slice(0)
      .reverse()
      .map((it) => {
        let curDate = dayjs(it.created_at).format("MMM DD, YYYY");
        let isNewDay = curDate != lastDate;
        if (isNewDay) lastDate = curDate;

        const isImage =
          it.documents?.[0]?.mime_type?.startsWith("image") || false;
        const isFile = !!it.documents?.[0]?.mime_type && !isImage; //anything but image

        return (
          <>
            {isNewDay && <MessageSeparator>{curDate}</MessageSeparator>}
            <Message
              key={it.id}
              model={{
                direction:
                  it.user?.role_id == ROLE_TALENT_ID
                    ? "incoming"
                    : it.user_id == user?.id
                      ? "outgoing"
                      : "incoming",
                position: "single",
              }}
            >
              {isImage ? (
                // <Message.ImageContent
                //   src={it.documents?.[0]?.original_url}
                //   width={200}
                //   onClick={() => console.log(it.documents?.[0]?.original_url)}
                // />
                <Message.HtmlContent
                  html={`<a href='${it.documents?.[0]?.original_url}' target='_blank'><img src='${it.documents?.[0]?.original_url}' style='max-width: 100%; width: ${isMobile ? "200" : "250"}px; height: auto;'/></a>`}
                />
              ) : isFile ? (
                <Message.HtmlContent
                  html={`${sanitizeMessageBody(it.body)}${it.body ? "<br/>" : ""}<a href='${it.documents?.[0]?.original_url}' target='_blank'>⬇️ Open File</a>`}
                />
              ) : (
                <Message.HtmlContent html={sanitizeMessageBody(it.body)} />
              )}
              <Message.Footer
                sender={`${it.user?.role_id == 5 ? `[👷]` : `[Manager]`} ${it.user?.full_name || "?"
                  }`}
                sentTime={dayjs(it.created_at).format("hh:mm A")}
              />
            </Message>
          </>
        );
      });
  }, [data, user, sanitizeMessageBody]);

  if (isError) {
    return (
      <Box
        sx={{
          px: 0,
          py: { xs: 1, sm: 1.5, md: 2 },
        }}
      >
        <Alert color="error" title="Error">
          {/* @ts-ignore */}
          {error?.data?.message}
        </Alert>
        {/* @ts-ignore */}
        {error.status == 403 && (
          <LoadingButton
            variant="contained"
            onClick={handleJoinChat}
            sx={{
              mt: { xs: 1.5, sm: 2 },
              px: { xs: 2, sm: 2.5 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
            loading={joining}
          >
            {joinChatLabel}
          </LoadingButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 0,
        py: { xs: 1, sm: 1.5, md: 2 },
      }}
    >
      <Stack
        sx={{
          alignItems: "flex-end",
          mb: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        <LoadingButton
          onClick={refetch}
          size={isMobile ? "small" : "medium"}
          loading={isFetching}
          startIcon={<Refresh />}
          sx={{
            alignSelf: "flex-end",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: { xs: 1.5, sm: 2, md: 2.5 },
            py: { xs: 0.75, sm: 1 },
            fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          Refresh
        </LoadingButton>
      </Stack>
      {chatId == 0 ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{
            mt: { xs: 1, sm: 2, md: 3 },
            mb: { xs: 1, sm: 1.5, md: 2 },
            px: { xs: 1, sm: 1.5, md: 2 },
          }}
        >
          <ChatOutlined
            sx={{
              fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.375rem" },
              color: "text.secondary",
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
              color: "text.secondary",
            }}
          >
            Chat not available yet
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            position: "relative",
            height: {
              xs: "calc(100vh - 240px)",
              sm: "calc(100vh - 280px)",
              md: "55vh"
            },
            minHeight: { xs: "350px", sm: "450px", md: "550px" },
            maxHeight: { xs: "calc(100vh - 200px)", sm: "65vh", md: "60vh" },
            overflow: "hidden",
            borderRadius: { xs: 1.5, sm: 2, md: 3 },
            border: (theme) =>
              `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          }}
        >
          <MainContainer
            style={{
              borderWidth: 0,
              borderRadius: 0,
              height: "100%",
              backgroundColor: "transparent",
            }}
          >
            <style>
              {`
              @media (max-width: 600px) {
                .cs-message__content {
                  padding: 8px 12px !important;
                  font-size: 0.875rem !important;
                }
                .cs-message__footer {
                  font-size: 0.75rem !important;
                  margin-top: 4px !important;
                }
                .cs-message-separator {
                  font-size: 0.75rem !important;
                  padding: 8px 0 !important;
                }
                .cs-message-input {
                  padding: 8px 12px !important;
                  font-size: 0.875rem !important;
                }
                .cs-message-input__content-editor {
                  font-size: 0.875rem !important;
                  padding: 6px 8px !important;
                }
                .cs-message-input__content-editor-wrapper {
                  min-height: 36px !important;
                }
                .cs-button {
                  padding: 6px 12px !important;
                  font-size: 0.875rem !important;
                }
              }
              @media (min-width: 601px) and (max-width: 960px) {
                .cs-message__content {
                  padding: 10px 14px !important;
                  font-size: 0.9375rem !important;
                }
                .cs-message__footer {
                  font-size: 0.8125rem !important;
                }
                .cs-message-separator {
                  font-size: 0.8125rem !important;
                }
                .cs-message-input {
                  padding: 10px 14px !important;
                }
                .cs-message-input__content-editor {
                  font-size: 0.9375rem !important;
                }
              }
            `}
            </style>
            <ChatContainer>
              <MessageList loading={isLoading}>{Messages}</MessageList>
              {[
                // ProjectStatus.PUBLISHED,
                ProjectStatus.SCHEDULED,
                ProjectStatus.IN_PROGRESS,
              ].includes(project.status) && (
                  <MessageInput
                    placeholder={typeMessageHereLabel}
                    attachButton={true}
                    onAttachClick={() => openFileSelector()}
                    onSend={onSend}
                    sendDisabled={sending}
                  />
                )}
            </ChatContainer>
          </MainContainer>
        </Box>
      )}
    </Box>
  );
};
export default ProjectMessagesPanel;
